import { useTranslation } from "react-i18next";
import { DeleteIcon } from "@/components/icons/action-icons";
import { SectionTitle } from "@/components/ui/SectionTitle";
import { Users2 } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { BlockHeader } from ".";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { startCase } from "lodash";
import { FieldLabel } from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import { useGetTeamRolesQuery } from "@/services/(company)/api.teams";
import type { ITeamMemberCard } from "./team-composer";
import { Spinner } from "@/components/ui/v2/spinner";
import { MemberAvatar } from "../../../_components/member-avatar";

import { formatDateDmyComma } from "@/utils/helper";

type OnRoleChange = (userId: number, roleId: number) => void;
type OnRemove = (userId: number, roleId: number | null) => void;

interface IProps {
  selectedMembers: ITeamMemberCard[];
  onRoleChange: OnRoleChange;
  onRemove: OnRemove;
  isLoading?: boolean;
}

export const RightBlock = ({
  selectedMembers,
  onRemove,
  onRoleChange,
  isLoading,
}: IProps) => {
  const { t } = useTranslation(["common"]);
  return (
    <div className="h-144 lg:h-152 min-w-64 shrink-0 flex-1 border border-primary rounded-sm overflow-hidden flex flex-col">
      <BlockHeader className="flex justify-between items-center">
        <SectionTitle title={t("common:a11y.team_members")} noMarginBottom />
        <MemberCount count={selectedMembers?.length} />
      </BlockHeader>
      <div className="p-4 flex-1 min-h-0 overflow-y-scroll">
        {isLoading ? (
          <Spinner />
        ) : selectedMembers?.length ? (
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
            {selectedMembers?.map((emp, i) => (
              <EmployeeCard
                key={i}
                employee={emp}
                updateMemberRole={onRoleChange}
                onRemove={onRemove}
              />
            ))}
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
    </div>
  );
};

const EmployeeCard = ({
  employee,
  updateMemberRole,
  onRemove,
}: {
  employee: ITeamMemberCard;
  updateMemberRole: OnRoleChange;
  onRemove: OnRemove;
}) => {
  const { t } = useTranslation(["common", "teams"]);
  const { data: { data: roles } = {}, isFetching: isFetchingRoles } =
    useGetTeamRolesQuery({});
  const cleanedUpName = startCase(employee?.employeeName?.toLowerCase());

  return (
    <div className="w-full border rounded p-4 h-max">
      <div className="flex gap-2">
        <MemberAvatar
          image={employee?.profileImage ?? undefined}
          isManager={employee?.isTeamManager}
        />
        <div className="w-full flex justify-between overflow-hidden">
          <div className="flex flex-col w-4/6">
            <p
              className="font-semibold text-sm mb-1 truncate"
              title={cleanedUpName}
            >
              {cleanedUpName}
            </p>
            <p
              className={`text-black/50 text-[10px] bg-primary/10 border rounded-full px-2 py-px max-w-max truncate ${employee?.isTeamManager && "bg-yellow-50 text-yellow-500"}`}
            >
              {employee?.position ?? t("teams:formation.no_position_assigned")}
            </p>
          </div>
          <Button
            size="icon"
            type="button"
            variant="secondary"
            aria-label={t("common:a11y.remove_member")}
            data-tooltip-id="hrm-tooltip"
            data-tooltip-content={t("common:a11y.remove_member")}
            onClick={() => onRemove(employee.userId, employee.roleId)}
            className="p-px text-danger border-none hover:bg-danger-muted shrink-0"
          >
            <DeleteIcon size={16} />
          </Button>
        </div>
      </div>

      <hr className="h-px w-full my-3 bg-border" />

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <FieldLabel htmlFor={`role-${employee.userId}`} className="text-xs">
            {t("teams:formation.team_role_label")}
          </FieldLabel>
          <Select
            value={employee.roleId ? String(employee.roleId) : undefined}
            onValueChange={(roleId) =>
              updateMemberRole(employee.userId, Number(roleId))
            }
          >
            <SelectTrigger
              id={`role-${employee.userId}`}
              className="p-2 h-8 text-xs"
            >
              <SelectValue placeholder={t("common:a11y.assign_role")} />
            </SelectTrigger>
            <SelectContent>
              {isFetchingRoles ? (
                <SelectItem disabled value="loading">
                  {t("teams:formation.loading")}
                </SelectItem>
              ) : (
                roles?.map((role, i) => (
                  <SelectItem key={i} value={String(role.id)}>
                    {role.roleName}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <FieldLabel className="text-xs">
            {t("teams:formation.date_added_label")}
          </FieldLabel>
          <p className="text-sm text-black/60">
            {employee?.createdAt
              ? employee?.createdAt
              : formatDateDmyComma(new Date())}
          </p>
        </div>
      </div>
    </div>
  );
};

const EmptyState = () => {
  const { t } = useTranslation(["teams"]);
  return (
    <div className="border rounded-sm p-4 flex flex-col items-center">
      <Avatar>
        <AvatarFallback className="bg-primary/10 rounded-full">
          <Users2 size={24} className="text-primary" />
        </AvatarFallback>
      </Avatar>
      <h6 className="text-base font-semibold mt-2">
        {t("teams:formation.empty_title")}
      </h6>
      <p className="text-black/50 -mt-1">
        {t("teams:formation.empty_message")}
      </p>
    </div>
  );
};

const MemberCount = ({ count }: { count: number }) =>
  !count ? null : (
    <div className="flex-none flex items-center justify-center min-w-7 h-7 px-1 bg-white border border-primary rounded-full text-primary text-xs font-bold tabular-nums shadow-xs">
      {count}
    </div>
  );
